/**
 * FinalizeRouteModal - Confirmation dialog for finalizing a route from the office-app.
 *
 * Used when a driver forgot to finalize the route in the field-app and the
 * Wasserij_items got stuck on Status='Opgehaald'. After confirmation, the
 * back-end moves all picked-up items to 'Klaar voor Voorinspectie' and tags
 * Mutatie_Bron__c='Office-App'.
 */

interface FinalizeRouteModalProps {
  open: boolean;
  routeName?: string | null;
  isFinalizing: boolean;
  onClose: () => void;
  onConfirm: () => void;
}

export function FinalizeRouteModal({
  open,
  routeName,
  isFinalizing,
  onClose,
  onConfirm,
}: FinalizeRouteModalProps) {
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div
        className="absolute inset-0 bg-black/50"
        onClick={isFinalizing ? undefined : onClose}
      />

      <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">
          Route afronden{routeName ? `: ${routeName}` : ''}
        </h3>

        <p className="text-sm text-gray-600 mb-3">
          Hiermee gaan alle opgehaalde kleden door naar{' '}
          <span className="font-medium">Klaar voor Voorinspectie</span> en wordt
          de route op <span className="font-medium">Afgerond</span> gezet.
        </p>

        <div className="mb-5 p-3 bg-amber-50 border border-amber-200 rounded-md">
          <p className="text-sm text-amber-800">
            <span className="font-medium">Let op:</span> doe dit alleen als de
            bus écht is uitgeladen in de wasserij. De kleden verschijnen direct
            in de Voorinspectie-lijst.
          </p>
        </div>

        <div className="flex gap-3 justify-end">
          <button
            type="button"
            onClick={onClose}
            disabled={isFinalizing}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
          >
            Annuleren
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={isFinalizing}
            className="px-4 py-2 text-sm font-medium text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {isFinalizing ? (
              <>
                <svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                Afronden...
              </>
            ) : (
              'Ja, afronden'
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
